function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
/* eslint-disable data-test/onClick */
import * as React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import { CloseButton } from '../CloseButton';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import styles from '@dtsl/css-inputbox/dist/index.module.css';
import Search from '@dtsl/icons/dist/icons/react/Search';

// eslint-disable-next-line react/display-name
const Inputbox = /*#__PURE__*/React.forwardRef((props, ref) => {
  const {
    labelText,
    specificationText,
    isError,
    disabled,
    identifier,
    placeholder,
    requiredLabel,
    inputType,
    tooltip,
    tooltipValue,
    multiline,
    inputStyles,
    className,
    readonly,
    wrapperStyles,
    clearable,
    maxCharCount,
    emojiButtonHtml,
    emojiPicker,
    suffixLabel,
    prefixButtonMetaData,
    suffixButtonMetaData,
    ...restprops
  } = props;
  const showEmojiPicker = /*#__PURE__*/React.isValidElement(emojiButtonHtml);
  const wrapperClasses = classNames({
    [styles['sib-input']]: true,
    [styles['sib-input-errored']]: isError,
    [styles['sib-input-inline-block']]: showEmojiPicker
  });
  const specificationClasses = classNames({
    [styles['sib-input-specification']]: true,
    [styles['error']]: isError
  });
  const maxLength = maxCharCount > 0 ? maxCharCount : null;
  const {
    value
  } = restprops;
  const getCharCountHtml = /*#__PURE__*/React.createElement("div", {
    className: styles['sib-inputbox__actions__count']
  }, /*#__PURE__*/React.createElement("span", {
    className: styles['sib-inputbox__actions__count--current']
  }, (value === null || value === void 0 ? void 0 : value.length) || 0), "/", /*#__PURE__*/React.createElement("span", {
    className: styles['sib-inputbox__actions__count--max']
  }, maxLength));
  const onReset = event => {
    event.stopPropagation();
    event.preventDefault();
    props.onReset();
  };
  const {
    onKeyPress = () => {}
  } = restprops;
  const handleOnKeyPress = event => {
    const code = 'charCode' in event ? event.charCode : event.keyCode;
    if (maxCharCount && (value === null || value === void 0 ? void 0 : value.length) === maxCharCount && code >= 32 && code <= 126) {
      return event.preventDefault();
    }
    onKeyPress(event);
  };
  restprops.onKeyPress = maxCharCount ? handleOnKeyPress : onKeyPress;
  const textAreaHTML = () => {
    const textAreaInputClass = classNames({
      [styles['sib-inputbox']]: true,
      [styles['sib-inputbox__with-count']]: !!maxLength,
      [styles['sib-inputbox__with-count-max']]: !!maxLength && maxLength >= 99,
      [className]: !!className
    });
    return /*#__PURE__*/React.createElement("div", {
      className: `${textAreaInputClass}`,
      disabled: disabled
    }, /*#__PURE__*/React.createElement("textarea", _extends({
      type: inputType,
      className: styles['input'],
      id: identifier,
      placeholder: placeholder,
      disabled: disabled,
      required: requiredLabel,
      style: inputStyles,
      readOnly: readonly,
      ref: ref,
      onKeyPress: handleOnKeyPress,
      maxLength: maxLength
    }, restprops)), /*#__PURE__*/React.createElement("div", {
      className: styles['sib-textarea-char-counter--placement']
    }, maxLength && getCharCountHtml));
  };
  const searchInputClass = classNames({
    [styles['sib-inputbox']]: true,
    [styles['sib-inputbox-search-container']]: true,
    [className]: !!className
  });
  const getInputHtml = () => {
    if (inputType === 'search') {
      return /*#__PURE__*/React.createElement("div", {
        className: searchInputClass,
        disabled: disabled,
        style: inputStyles
      }, /*#__PURE__*/React.createElement("span", {
        className: styles['sib-inputbox-search-icon']
      }, /*#__PURE__*/React.createElement(Search, null)), /*#__PURE__*/React.createElement("input", _extends({
        type: inputType,
        className: styles['input'],
        id: identifier,
        placeholder: placeholder,
        disabled: disabled,
        required: requiredLabel,
        readOnly: readonly,
        ref: ref
      }, restprops)), clearable && /*#__PURE__*/React.createElement(CloseButton, {
        type: "reset",
        ariaLabel: "Reset search button",
        onClick: onReset,
        className: styles['search-clear'],
        disabled: disabled,
        size: "small",
        onFocus: event => {
          event.stopPropagation();
        }
      }));
    }
    const textInputClass = classNames({
      [styles['sib-inputbox']]: true,
      [styles['sib-inputbox__with-action-and-count']]: !!showEmojiPicker && !!maxLength,
      [styles['sib-inputbox__with-action-btn']]: !!showEmojiPicker && !maxLength,
      [styles['sib-inputbox__with-count']]: !!maxLength && !showEmojiPicker,
      [styles['sib-inputbox__with-count-max']]: !!maxLength && maxLength >= 99,
      [styles['sib-inputbox__with-prefix-suffix-actions']]: (prefixButtonMetaData || suffixButtonMetaData) && inputType === 'number',
      [styles['sib-inputbox__with-prefix-actions']]: prefixButtonMetaData && inputType === 'number',
      [styles['sib-inputbox__with-suffix-actions']]: suffixButtonMetaData && inputType === 'number',
      [className]: !!className
    });
    const dualButtonsWithSuffixLabelHTML = () => {
      const {
        className: prefixBtnClassName,
        ...prefixBtnRestProps
      } = prefixButtonMetaData || {};
      const {
        className: suffixBtnClassName,
        ...suffixBtnRestProps
      } = suffixButtonMetaData || {};
      const suffixBtnClasses = classNames({
        [styles['sib-inputbox__suffix-container--btn']]: true,
        [suffixBtnClassName]: !!suffixBtnClassName
      });
      const prefixBtnClasses = classNames({
        [styles['sib-inputbox__prefix-container--btn']]: true,
        [prefixBtnClassName]: !!prefixBtnClassName
      });
      return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
        className: styles['sib-inputbox__prefix-container']
      }, prefixButtonMetaData && /*#__PURE__*/React.createElement(Button, _extends({
        variant: "tertiary",
        iconAlone: true,
        size: "small",
        disabled: disabled || readonly
      }, prefixBtnRestProps, {
        className: prefixBtnClasses
      }))), /*#__PURE__*/React.createElement("div", {
        className: `${styles['sib-inputbox__suffix-container']} ${!suffixLabel ? styles['without-suffix-label'] : ''}`
      }, suffixLabel && /*#__PURE__*/React.createElement(Text, {
        tag: "span",
        size: "md",
        className: styles['sib-inputbox__suffix-container--label'],
        color: "var(--sib-color_content-secondary)"
      }, suffixLabel), suffixButtonMetaData && /*#__PURE__*/React.createElement(Button, _extends({
        variant: "tertiary",
        iconAlone: true,
        size: "small",
        disabled: disabled || readonly
      }, suffixBtnRestProps, {
        className: suffixBtnClasses
      }))));
    };
    if ((prefixButtonMetaData || suffixButtonMetaData) && inputType === 'number') {
      return /*#__PURE__*/React.createElement("div", {
        className: textInputClass,
        disabled: disabled
      }, dualButtonsWithSuffixLabelHTML(), /*#__PURE__*/React.createElement("input", _extends({}, restprops, {
        type: inputType,
        className: styles['input'],
        id: identifier,
        placeholder: placeholder,
        disabled: disabled,
        required: requiredLabel,
        style: {
          ...inputStyles
        },
        ref: ref,
        readOnly: readonly,
        onKeyPress: handleOnKeyPress,
        maxLength: maxLength
      })));
    }
    const getActionButtonsHtml = () => {
      if (inputType !== 'text' || readonly || disabled || !(showEmojiPicker || maxLength)) return '';
      return /*#__PURE__*/React.createElement("div", {
        className: styles['sib-inputbox__actions']
      }, showEmojiPicker && emojiButtonHtml, maxLength && getCharCountHtml);
    };
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: textInputClass,
      disabled: disabled
    }, /*#__PURE__*/React.createElement("input", _extends({
      type: inputType,
      className: styles['input'],
      id: identifier,
      placeholder: placeholder,
      disabled: disabled,
      required: requiredLabel,
      style: inputStyles,
      ref: ref,
      readOnly: readonly,
      onKeyPress: handleOnKeyPress,
      maxLength: maxLength
    }, restprops)), getActionButtonsHtml()));
  };
  const searchboxHTML = tooltip ? /*#__PURE__*/React.createElement("div", {
    className: styles['sib-input'],
    style: wrapperStyles
  }, /*#__PURE__*/React.createElement(Tooltip, tooltipValue, getInputHtml())) : /*#__PURE__*/React.createElement("div", {
    className: styles['sib-input'],
    style: wrapperStyles
  }, getInputHtml());
  const inputboxHTML = /*#__PURE__*/React.createElement("div", {
    className: wrapperClasses,
    style: wrapperStyles
  }, labelText ? /*#__PURE__*/React.createElement("label", {
    className: styles['sib-input-label'],
    htmlFor: identifier
  }, labelText, requiredLabel && /*#__PURE__*/React.createElement("span", {
    className: styles['required']
  }, "*")) : null, multiline ? textAreaHTML() : getInputHtml(), emojiPicker, specificationText && /*#__PURE__*/React.createElement("p", {
    className: specificationClasses,
    id: isError ? 'sib-inputbox-specification-text-error' : 'sib-inputbox-specification-text'
  }, specificationText));
  return inputType === 'search' ? searchboxHTML : inputboxHTML;
});
Inputbox.propTypes = {
  labelText: PropTypes.string,
  isError: PropTypes.bool,
  specificationText: PropTypes.string,
  requiredLabel: PropTypes.bool,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  identifier: PropTypes.string,
  inputType: PropTypes.string,
  tooltip: PropTypes.bool,
  tooltipValue: PropTypes.object,
  wrapperStyles: PropTypes.object,
  inputStyles: PropTypes.object,
  multiline: PropTypes.bool,
  className: PropTypes.string,
  readonly: PropTypes.bool,
  onReset: PropTypes.func,
  clearable: PropTypes.bool,
  maxCharCount: PropTypes.number,
  emojiButtonHtml: PropTypes.node,
  emojiPicker: PropTypes.node,
  prefixButtonMetaData: PropTypes.shape({
    dataTestId: PropTypes.string,
    ariaLabel: PropTypes.string,
    iconAlone: PropTypes.bool,
    icon: PropTypes.element,
    className: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium']),
    variant: PropTypes.oneOf(['default', 'primary', 'secondary', 'tertiary', 'destructive', 'destructive-secondary', 'destructive-tertiary', 'upgrade', 'upgrade-secondary', 'upgrade-tertiary']),
    onClick: PropTypes.func,
    label: PropTypes.string,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    loading: PropTypes.bool
  }),
  suffixButtonMetaData: PropTypes.shape({
    dataTestId: PropTypes.string,
    ariaLabel: PropTypes.string,
    iconAlone: PropTypes.bool,
    icon: PropTypes.element,
    className: PropTypes.string,
    size: PropTypes.oneOf(['small', 'medium']),
    variant: PropTypes.oneOf(['default', 'primary', 'secondary', 'tertiary', 'destructive', 'destructive-secondary', 'destructive-tertiary', 'upgrade', 'upgrade-secondary', 'upgrade-tertiary']),
    onClick: PropTypes.func,
    label: PropTypes.string,
    ellipsis: PropTypes.bool,
    disabled: PropTypes.bool,
    tooltip: PropTypes.bool,
    tooltipValue: PropTypes.object,
    dropdownOpen: PropTypes.bool,
    showArrowIcon: PropTypes.bool,
    loading: PropTypes.bool
  }),
  suffixLabel: PropTypes.string
};
Inputbox.defaultProps = {
  labelText: null,
  isError: false,
  specificationText: '',
  requiredLabel: false,
  disabled: false,
  placeholder: 'placeholder',
  identifier: '',
  inputType: 'text',
  tooltip: false,
  tooltipValue: {},
  inputStyles: {},
  wrapperStyles: {},
  multiline: false,
  readonly: false,
  onReset: () => {},
  clearable: true,
  maxCharCount: null,
  emojiButtonHtml: null,
  emojiPicker: null,
  prefixButtonMetaData: null,
  suffixButtonMetaData: null,
  suffixLabel: ''
};
export default Inputbox;